import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Statistic, Table, Tag, Button, Modal, Form, Input, InputNumber,
  message, Typography, Tabs, Space, Tooltip, Divider
} from 'antd';
import {
  GiftOutlined, CopyOutlined, DollarOutlined, TeamOutlined,
  WalletOutlined, SendOutlined, InfoCircleOutlined
} from '@ant-design/icons';
import { useAuthStore } from '../stores/authStore';
import referralService, {
  ReferralOverview, InviteItem, CommissionItem, WithdrawalItem
} from '../services/referral';

const { Title, Text, Paragraph } = Typography;

const formatCents = (cents: number) => `$${(cents / 100).toFixed(2)}`;

const statusColors: Record<string, string> = {
  pending: 'orange',
  settled: 'green',
  withdrawn: 'blue',
  cancelled: 'red',
  approved: 'cyan',
  rejected: 'red',
  completed: 'green',
};

const statusLabels: Record<string, string> = {
  pending: '待结算',
  settled: '已结算',
  withdrawn: '已提现',
  cancelled: '已取消',
  approved: '已通过',
  rejected: '已驳回',
  completed: '已完成',
};

const Referral: React.FC = () => {
  const { user } = useAuthStore();
  const [overview, setOverview] = useState<ReferralOverview | null>(null);
  const [invites, setInvites] = useState<InviteItem[]>([]);
  const [invitesTotal, setInvitesTotal] = useState(0);
  const [commissions, setCommissions] = useState<CommissionItem[]>([]);
  const [commissionsTotal, setCommissionsTotal] = useState(0);
  const [withdrawals, setWithdrawals] = useState<WithdrawalItem[]>([]);
  const [withdrawalsTotal, setWithdrawalsTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [withdrawModalVisible, setWithdrawModalVisible] = useState(false);
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [form] = Form.useForm();

  const promotionBaseUrl = (import.meta.env.VITE_PROMOTION_URL as string | undefined) || 'https://console.ipcloak.ai/register';

  const inviteCode = overview?.invite_code || '';
  const inviteLink = inviteCode ? `${promotionBaseUrl}?invite=${encodeURIComponent(inviteCode)}` : '';

  const loadOverview = useCallback(async () => {
    try {
      const data = await referralService.getOverview();
      setOverview(data);
    } catch {
      // ignore
    }
  }, []);

  const loadInvites = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const data = await referralService.getInvites(page, 10);
      setInvites(data.list || []);
      setInvitesTotal(data.total);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCommissions = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const data = await referralService.getCommissions('', page, 10);
      setCommissions(data.list || []);
      setCommissionsTotal(data.total);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  const loadWithdrawals = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const data = await referralService.getWithdrawals(page, 10);
      setWithdrawals(data.list || []);
      setWithdrawalsTotal(data.total);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
    loadInvites();
    loadCommissions();
    loadWithdrawals();
  }, [loadOverview, loadInvites, loadCommissions, loadWithdrawals]);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success('已复制到剪贴板');
    });
  };

  const handleWithdraw = async () => {
    try {
      const values = await form.validateFields();
      setWithdrawLoading(true);
      await referralService.requestWithdrawal(Math.round(values.amount * 100), values.wallet_address);
      message.success('提现申请已提交');
      setWithdrawModalVisible(false);
      form.resetFields();
      loadOverview();
      loadWithdrawals();
    } catch (err: any) {
      if (err?.response?.data?.message) {
        message.error(err.response.data.message);
      }
    } finally {
      setWithdrawLoading(false);
    }
  };

  const wallet = overview?.wallet;

  const inviteColumns = [
    { title: '用户', dataIndex: 'username_masked', key: 'username' },
    { title: '注册时间', dataIndex: 'registered_at', key: 'registered_at', render: (t: string) => t ? new Date(t).toLocaleString() : '-' },
    { title: '已付费', dataIndex: 'has_paid', key: 'has_paid', render: (v: boolean) => v ? <Tag color="green">是</Tag> : <Tag>否</Tag> },
    { title: '贡献佣金', dataIndex: 'total_commission_cents', key: 'commission', render: (v: number) => formatCents(v) },
  ];

  const commissionColumns = [
    { title: '被邀请人', dataIndex: 'referee_username_masked', key: 'referee' },
    { title: '订单金额', dataIndex: 'order_amount_cents', key: 'order', render: (v: number) => formatCents(v) },
    { title: '比例', dataIndex: 'commission_rate', key: 'rate', render: (v: number) => `${v}%` },
    { title: '佣金', dataIndex: 'commission_amount_cents', key: 'amount', render: (v: number) => <Text strong style={{ color: '#52c41a' }}>{formatCents(v)}</Text> },
    { title: '状态', dataIndex: 'status', key: 'status', render: (s: string) => <Tag color={statusColors[s] || 'default'}>{statusLabels[s] || s}</Tag> },
    { title: '时间', dataIndex: 'created_at', key: 'created_at', render: (t: string) => t ? new Date(t).toLocaleString() : '-' },
  ];

  const withdrawalColumns = [
    { title: '金额', dataIndex: 'amount_cents', key: 'amount', render: (v: number) => formatCents(v) },
    { title: '收款地址', dataIndex: 'wallet_address', key: 'address', ellipsis: true, render: (v: string) => <Tooltip title={v}><Text copyable={{ text: v }}>{v.slice(0, 8)}...{v.slice(-6)}</Text></Tooltip> },
    { title: '状态', dataIndex: 'status', key: 'status', render: (s: string) => <Tag color={statusColors[s] || 'default'}>{statusLabels[s] || s}</Tag> },
    { title: 'TX Hash', dataIndex: 'tx_hash', key: 'tx_hash', ellipsis: true, render: (v: string) => v || '-' },
    { title: '申请时间', dataIndex: 'created_at', key: 'created_at', render: (t: string) => t ? new Date(t).toLocaleString() : '-' },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={3}><GiftOutlined /> 推广返佣</Title>

      {/* 推广链接卡片 */}
      <Card style={{ marginBottom: 24, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none' }}>
        <Row gutter={24} align="middle">
          <Col flex="auto">
            <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14 }}>您的推广链接</Text>
            <Paragraph style={{ color: '#fff', fontSize: 16, marginBottom: 4 }} copyable={{ text: inviteLink, tooltips: ['复制', '已复制'] }}>
              {inviteLink || '加载中...'}
            </Paragraph>
            <Space>
              <Tag color="gold">邀请码: {inviteCode || '...'}</Tag>
              <Tag color="lime">返佣比例: {overview?.commission_rate || 30}%</Tag>
            </Space>
          </Col>
          <Col>
            <Button type="primary" ghost icon={<CopyOutlined />} onClick={() => handleCopy(inviteLink)} style={{ borderColor: '#fff', color: '#fff' }}>
              复制链接
            </Button>
          </Col>
        </Row>
      </Card>

      {/* 钱包概览 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={5}>
          <Card>
            <Statistic title="累计佣金" value={wallet ? wallet.total_earned_cents / 100 : 0} precision={2} prefix={<DollarOutlined />} />
          </Card>
        </Col>
        <Col span={5}>
          <Card>
            <Statistic title={<>待结算 <Tooltip title="支付成功后冻结7天"><InfoCircleOutlined /></Tooltip></>} value={wallet ? wallet.pending_cents / 100 : 0} precision={2} prefix="$" valueStyle={{ color: '#faad14' }} />
          </Card>
        </Col>
        <Col span={5}>
          <Card>
            <Statistic title="可提现" value={wallet ? wallet.available_cents / 100 : 0} precision={2} prefix="$" valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col span={5}>
          <Card>
            <Statistic title="提现中" value={wallet ? wallet.frozen_cents / 100 : 0} precision={2} prefix="$" valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic title="已提现" value={wallet ? wallet.withdrawn_cents / 100 : 0} precision={2} prefix="$" />
          </Card>
        </Col>
      </Row>

      {/* 邀请统计 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card>
            <Statistic title="邀请人数" value={overview?.total_invites || 0} prefix={<TeamOutlined />} />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Button type="primary" icon={<WalletOutlined />} size="large" block
              disabled={!wallet || wallet.available_cents <= 0}
              onClick={() => setWithdrawModalVisible(true)}
            >
              申请提现
            </Button>
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Text type="secondary">提现方式: USDT (TRC20)</Text><br />
            <Text type="secondary">最低提现: $10</Text>
          </Card>
        </Col>
      </Row>

      {/* 详情 Tabs */}
      <Card>
        <Tabs defaultActiveKey="invites" items={[
          {
            key: 'invites',
            label: `邀请列表 (${invitesTotal})`,
            children: (
              <Table
                columns={inviteColumns}
                dataSource={invites}
                rowKey="username_masked"
                loading={loading}
                pagination={{ total: invitesTotal, pageSize: 10, onChange: (p) => loadInvites(p) }}
                size="small"
              />
            ),
          },
          {
            key: 'commissions',
            label: `佣金明细 (${commissionsTotal})`,
            children: (
              <Table
                columns={commissionColumns}
                dataSource={commissions}
                rowKey="id"
                loading={loading}
                pagination={{ total: commissionsTotal, pageSize: 10, onChange: (p) => loadCommissions(p) }}
                size="small"
              />
            ),
          },
          {
            key: 'withdrawals',
            label: `提现记录 (${withdrawalsTotal})`,
            children: (
              <Table
                columns={withdrawalColumns}
                dataSource={withdrawals}
                rowKey="id"
                loading={loading}
                pagination={{ total: withdrawalsTotal, pageSize: 10, onChange: (p) => loadWithdrawals(p) }}
                size="small"
              />
            ),
          },
        ]} />
      </Card>

      {/* 提现弹窗 */}
      <Modal
        title={<><WalletOutlined /> 申请提现</>}
        open={withdrawModalVisible}
        onCancel={() => setWithdrawModalVisible(false)}
        onOk={handleWithdraw}
        confirmLoading={withdrawLoading}
        okText="提交申请"
      >
        <Divider />
        <Text>可提现余额: <Text strong style={{ color: '#52c41a' }}>{formatCents(wallet?.available_cents || 0)}</Text></Text>
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="amount" label="提现金额 (USD)" rules={[
            { required: true, message: '请输入提现金额' },
            { type: 'number', min: 10, message: '最低提现 $10' },
          ]}>
            <InputNumber style={{ width: '100%' }} min={10} max={wallet ? wallet.available_cents / 100 : 0} precision={2} prefix="$" />
          </Form.Item>
          <Form.Item name="wallet_address" label="USDT 收款地址 (TRC20)" rules={[
            { required: true, message: '请输入 TRC20 钱包地址' },
            { pattern: /^T[1-9A-HJ-NP-Za-km-z]{33}$/, message: 'TRC20 地址格式不正确' },
          ]}>
            <Input placeholder="T..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Referral;
